import { Alert, AlertTitle, Autocomplete, Backdrop, Box, Button, CircularProgress, FormControlLabel, Grid, MenuItem, Radio, RadioGroup, Tab, Tabs, TextField, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import RequestStatusAlert from '../../lib/components/RequestStatusAlert';
import { downloadFile } from '../../lib/services/customQueryService';
import {
    customDataProductsAccessRulesFileName,
    dataAccessGroupsBulkTemplateEmptyFileName,
    dataAccessGroupsBulkTemplateExistingFileName,
    dataProductsAccessRulesFileName
} from '../../lib/services/constants';
import doRequest from '../../lib/services/apiRequestor';

function DataAccessGroupPanel({ value, index, idToken }) {
    const [groups, setGroups] = useState([]);
    const [aladdinGroups, setAladdinGroups] = useState([]);
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [isNew, setIsNew] = useState(false);
    const [loading, setLoading] = useState(false);
    const [statusMessage, setStatusMessage] = useState(null);
    const [severity, setSeverity] = useState(null);
    const [bulkTemplateMode, setBulkTemplateMode] = useState('empty');
    const [bulkUploadFile, setBulkUploadFile] = useState(null);
    const [bulkErrorMessage, setBulkErrorMessage] = useState(null);
    const [bulkResultMessage, setBulkResultMessage] = useState(null);
    const [accessGrants, setAccessGrants] = useState({ productGrants: [], domainGrants: [] });
    const [availableProducts, setAvailableProducts] = useState([]);
    const [availableDomains, setAvailableDomains] = useState([]);
    const [selectedProductToGrant, setSelectedProductToGrant] = useState(null);
    const [selectedDomainToGrant, setSelectedDomainToGrant] = useState(null);
    const [grantStatusMessage, setGrantStatusMessage] = useState(null);
    const [grantSeverity, setGrantSeverity] = useState(null);
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    useEffect(() => {
        if (value === index && idToken) {
            fetchGroups();
            fetchAladdinGroups();
            fetchAvailableProducts();
            fetchAvailableDomains();
        }
    }, [value, index, idToken]);

    const fetchGroups = async () => {
        setLoading(true);
        const response = await doRequest('/api/DataAccessGroups', idToken);
        if (!response.isError) {
            setGroups(response.data || []);
        }
        setLoading(false);
    };

    const fetchAladdinGroups = async () => {
        const response = await doRequest('/api/dataaccessgroups/aladdingroups', idToken);
        if (!response.isError) {
            setAladdinGroups(response.data || []);
        }
    };

    const fetchAvailableProducts = async () => {
        const response = await doRequest('/api/DataProducts', idToken);
        if (!response.isError) {
            setAvailableProducts(response.data || []);
        }
    };

    const fetchAvailableDomains = async () => {
        const response = await doRequest('/api/dataaccessgroups/domains', idToken);
        if (!response.isError) {
            setAvailableDomains(response.data || []);
        }
    };

    const fetchAccessGrants = async (groupId) => {
        const response = await doRequest(`/api/dataaccessgroups/${groupId}/access`, idToken);
        if (!response.isError) {
            setAccessGrants(response.data || { productGrants: [], domainGrants: [] });
        }
    };

    const handleGroupSelect = (groupId) => {
        const group = groups.find(g => g.id === groupId);
        if (!group) return;

        setSelectedGroup({
            ...group,
            associatedAladdinGroups: Array.isArray(group.associatedAladdinGroups) ? group.associatedAladdinGroups : [],
            coOwners: Array.isArray(group.coOwners) ? group.coOwners : [],
            accessGroupApprovers: Array.isArray(group.accessGroupApprovers) ? group.accessGroupApprovers : []
        });
        setIsNew(false);
        setStatusMessage(null);
        setGrantStatusMessage(null);
        setSelectedProductToGrant(null);
        setSelectedDomainToGrant(null);
        fetchAccessGrants(groupId);
    };

    const handleNewGroup = () => {
        setSelectedGroup({
            name: '',
            orgLevel: '',
            type: '',
            owner: '',
            associatedARCRole: '',
            associatedADGroup: '',
            associatedAladdinGroups: [],
            associatedSnowflakeRole: '',
            associatedSnowflakeArcRoleName: '',
            description: '',
            coOwners: [],
            accessGroupApprovers: []
        });
        setIsNew(true);
        setStatusMessage(null);
        setAccessGrants({ productGrants: [], domainGrants: [] });
        setGrantStatusMessage(null);
    };

    const handleInputChange = (field, fieldValue) => {
        setSelectedGroup(prev => ({ ...prev, [field]: fieldValue }));
    };

    const updateContact = (field, indexToUpdate, property, value) => {
        const nextContacts = (selectedGroup[field] || []).map((contact, index) =>
            index === indexToUpdate ? { ...contact, [property]: value } : contact);
        handleInputChange(field, nextContacts);
    };

    const addContact = (field) => {
        const nextContacts = [...(selectedGroup[field] || []), { email: '', firstName: '', name: '' }];
        handleInputChange(field, nextContacts);
    };

    const removeContact = (field, indexToRemove) => {
        const nextContacts = (selectedGroup[field] || []).filter((_, index) => index !== indexToRemove);
        handleInputChange(field, nextContacts);
    };

    const validateContacts = (contacts, label) => {
        const seenEmails = new Set();
        for (const contact of contacts || []) {
            const email = (contact.email || '').trim().toLowerCase();
            if (!email) {
                return `${label} email is required.`;
            }
            if (!emailRegex.test(email)) {
                return `${label} email is invalid: ${contact.email}`;
            }
            if (seenEmails.has(email)) {
                return `Duplicate ${label.toLowerCase()} email found: ${contact.email}`;
            }
            seenEmails.add(email);
        }
        return null;
    };

    const handleSave = async () => {
        const coOwnerValidationError = validateContacts(selectedGroup.coOwners, 'Co-owner');
        if (coOwnerValidationError) {
            setStatusMessage(coOwnerValidationError);
            setSeverity('error');
            return;
        }

        const approverValidationError = validateContacts(selectedGroup.accessGroupApprovers, 'Approver');
        if (approverValidationError) {
            setStatusMessage(approverValidationError);
            setSeverity('error');
            return;
        }

        setLoading(true);
        setStatusMessage(null);

        const method = isNew ? 'POST' : 'PUT';
        const endpoint = isNew ? '/api/DataAccessGroups' : `/api/DataAccessGroups?groupId=${selectedGroup.id}`;

        const response = await doRequest(endpoint, idToken, {
            method,
            body: JSON.stringify(selectedGroup)
        });

        if (!response.isError) {
            setStatusMessage('Saved successfully');
            setSeverity('success');
            await fetchGroups();
            if (isNew) {
                setIsNew(false);
            }
        } else {
            setStatusMessage('Error saving group: ' + response.data);
            setSeverity('error');
        }
        setLoading(false);
    };

    const handleDelete = async () => {
        if (!window.confirm('Are you sure you want to delete this access group?')) return;

        setLoading(true);
        const response = await doRequest(`/api/DataAccessGroups/${selectedGroup.id}`, idToken, {
            method: 'DELETE'
        });

        if (!response.isError) {
            setStatusMessage('Deleted successfully');
            setSeverity('success');
            setSelectedGroup(null);
            await fetchGroups();
        } else {
            setStatusMessage('Error deleting group: ' + response.data);
            setSeverity('error');
        }
        setLoading(false);
    };

    const handleGrantProduct = async () => {
        if (!selectedProductToGrant) return;
        setLoading(true);
        setGrantStatusMessage(null);
        const response = await doRequest(`/api/dataaccessgroups/${selectedGroup.id}/products`, idToken, {
            method: 'POST',
            body: JSON.stringify({ dataProductId: selectedProductToGrant.id })
        });
        if (!response.isError) {
            setGrantStatusMessage('Product access granted.');
            setGrantSeverity('success');
            setSelectedProductToGrant(null);
            await fetchAccessGrants(selectedGroup.id);
        } else {
            setGrantStatusMessage('Failed to grant product access.');
            setGrantSeverity('error');
        }
        setLoading(false);
    };

    const handleRevokeProduct = async (productId) => {
        if (!window.confirm(`Remove product access grant for "${productId}"?`)) return;

        setLoading(true);
        setGrantStatusMessage(null);
        const response = await doRequest(`/api/dataaccessgroups/${selectedGroup.id}/products/${productId}`, idToken, {
            method: 'DELETE'
        });
        if (!response.isError) {
            setGrantStatusMessage('Product access revoked.');
            setGrantSeverity('success');
            await fetchAccessGrants(selectedGroup.id);
        } else {
            setGrantStatusMessage('Failed to revoke product access.');
            setGrantSeverity('error');
        }
        setLoading(false);
    };

    const handleGrantDomain = async () => {
        if (!selectedDomainToGrant) return;
        setLoading(true);
        setGrantStatusMessage(null);
        const response = await doRequest(`/api/dataaccessgroups/${selectedGroup.id}/domains`, idToken, {
            method: 'POST',
            body: JSON.stringify({ domainName: selectedDomainToGrant })
        });
        if (!response.isError) {
            setGrantStatusMessage('Domain access granted.');
            setGrantSeverity('success');
            setSelectedDomainToGrant(null);
            await fetchAccessGrants(selectedGroup.id);
        } else {
            setGrantStatusMessage('Failed to grant domain access.');
            setGrantSeverity('error');
        }
        setLoading(false);
    };

    const handleRevokeDomain = async (domainName) => {
        if (!window.confirm(`Remove domain access grant for "${domainName}"?`)) return;

        setLoading(true);
        setGrantStatusMessage(null);
        const response = await doRequest(`/api/dataaccessgroups/${selectedGroup.id}/domains/${encodeURIComponent(domainName)}`, idToken, {
            method: 'DELETE'
        });
        if (!response.isError) {
            setGrantStatusMessage('Domain access revoked.');
            setGrantSeverity('success');
            await fetchAccessGrants(selectedGroup.id);
        } else {
            setGrantStatusMessage('Failed to revoke domain access.');
            setGrantSeverity('error');
        }
        setLoading(false);
    };

    const validateBulkUploadFile = () => {
        if (bulkUploadFile === null) {
            setBulkErrorMessage('Please select a .csv file to upload.');
            return false;
        }

        const fileName = (bulkUploadFile.name || '').toLowerCase();
        if (!fileName.endsWith('.csv')) {
            setBulkErrorMessage('Please upload a valid .csv file.');
            return false;
        }

        return true;
    };

    const setBulkFileToUpload = (event) => {
        setBulkUploadFile(event.target.files?.[0] || null);
    };

    const getBulkTemplateFileName = () => (bulkTemplateMode === 'existing'
        ? dataAccessGroupsBulkTemplateExistingFileName
        : dataAccessGroupsBulkTemplateEmptyFileName);

    const downloadBulkTemplate = async () => {
        setBulkErrorMessage(null);
        setBulkResultMessage(null);
        setLoading(true);

        const response = await doRequest(`/api/dataaccessgroups/bulk/template?mode=${bulkTemplateMode}`, idToken, {
            method: 'GET'
        });

        if (!response.isError) {
            downloadFile({ content: response.data, fileName: getBulkTemplateFileName() });
            setBulkResultMessage('Template downloaded successfully.');
        } else {
            setBulkErrorMessage('Failed to download template.');
        }
        setLoading(false);
    };

    const getBulkUploadResultSummary = (result) => {
        if (!result) {
            return 'Bulk upload completed.';
        }

        return `Bulk upload completed. Rows processed: ${result.totalRowsProcessed || 0}, groups created: ${result.groupsCreated || 0}, groups updated: ${result.groupsUpdated || 0}, failed rows: ${result.failedRows || 0}.`;
    };

    const uploadBulkTemplate = async () => {
        setBulkErrorMessage(null);
        setBulkResultMessage(null);
        if (!validateBulkUploadFile()) return;

        setLoading(true);
        const response = await doRequest('/api/dataaccessgroups/bulk/upload', idToken, {
            method: 'POST',
            body: bulkUploadFile,
            headers: {
                'content-type': bulkUploadFile.type || 'text/csv'
            }
        });

        if (!response.isError) {
            const result = response.data;
            const summary = getBulkUploadResultSummary(result);
            const hasErrors = (result?.failedRows || 0) > 0 || (result?.errors?.length || 0) > 0;
            if (hasErrors) {
                const firstError = result?.errors?.[0];
                setBulkErrorMessage(`${summary}${firstError ? ` First error (row ${firstError.rowNumber}): ${firstError.message}` : ''}`);
            } else {
                setBulkResultMessage(summary);
            }
            await fetchGroups();
        } else {
            const responseData = response.data;
            if (responseData && responseData.errors && responseData.errors.length > 0) {
                const firstError = responseData.errors[0];
                setBulkErrorMessage(`${getBulkUploadResultSummary(responseData)} First error (row ${firstError.rowNumber}): ${firstError.message}`);
            } else {
                setBulkErrorMessage('Bulk upload failed.');
            }
        }
        setLoading(false);
    };

    return (
        <Grid item xs={12} hidden={value !== index}>
            {value === index && (
                <Grid container spacing={2}>
                    <Grid item xs={10}>
                        <Autocomplete
                            options={groups.map(g => g.id)}
                            getOptionLabel={(option) => {
                                const group = groups.find(g => g.id === option);
                                return group ? `${group.name}` : option;
                            }}
                            renderInput={(params) => <TextField {...params} label="Select Access Group" />}
                            onChange={(event, selectedValue) => selectedValue && handleGroupSelect(selectedValue)}
                            loading={loading}
                        />
                    </Grid>
                    <Grid item xs={2}>
                        <Button variant="contained" fullWidth onClick={handleNewGroup} sx={{ marginTop: '7px' }}>
                            New Group
                        </Button>
                    </Grid>

                    {statusMessage && (
                        <Grid item xs={12}>
                            <RequestStatusAlert message={statusMessage} severity={severity} />
                        </Grid>
                    )}

                    <Grid item xs={12} sx={{ marginTop: 1 }}>
                        <Typography variant="h6">Bulk Upload</Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="body2">Download an empty or pre-filled template, bulk edit in CSV, then upload.</Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            select
                            label="Template mode"
                            fullWidth
                            value={bulkTemplateMode}
                            onChange={(event) => setBulkTemplateMode(event.target.value)}
                        >
                            <MenuItem value="empty">Empty template</MenuItem>
                            <MenuItem value="existing">Template with existing groups and mappings</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid item xs={12} md={6} sx={{ display: 'flex', alignItems: 'center' }}>
                        <Button variant="outlined" onClick={downloadBulkTemplate} disabled={loading}>
                            Download Template
                        </Button>
                    </Grid>
                    <Grid item xs={12}>
                        <input type="file" id="bulk-upload-file" accept=".csv" onChange={setBulkFileToUpload} />
                    </Grid>
                    <Grid item xs={12}>
                        <Button variant="contained" onClick={uploadBulkTemplate} disabled={loading}>Upload Bulk File</Button>
                    </Grid>
                    {bulkErrorMessage && (
                        <Grid item xs={12}>
                            <RequestStatusAlert message={bulkErrorMessage} severity="error" />
                        </Grid>
                    )}
                    {bulkResultMessage && (
                        <Grid item xs={12}>
                            <RequestStatusAlert message={bulkResultMessage} severity="success" />
                        </Grid>
                    )}

                    {selectedGroup && (
                        <>
                            <Grid item xs={12}>
                                <Typography variant="h6">Access Group Details</Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    label="Access Group Name"
                                    fullWidth
                                    value={selectedGroup.name || ''}
                                    required
                                    onChange={(event) => handleInputChange('name', event.target.value)}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    select
                                    label="Access Group Org Level"
                                    fullWidth
                                    value={selectedGroup.orgLevel || ''}
                                    onChange={(event) => handleInputChange('orgLevel', event.target.value)}
                                >
                                    <MenuItem value="">None</MenuItem>
                                    <MenuItem value="Excom Area">Excom Area</MenuItem>
                                    <MenuItem value="Excom-1">Excom-1</MenuItem>
                                    <MenuItem value="Excom-2">Excom-2</MenuItem>
                                    <MenuItem value="Excom-3">Excom-3</MenuItem>
                                    <MenuItem value="Supervisory Org">Supervisory Org</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    select
                                    label="Type"
                                    fullWidth
                                    value={selectedGroup.type || ''}
                                    onChange={(event) => handleInputChange('type', event.target.value)}
                                >
                                    <MenuItem value="BusinessArea">BusinessArea</MenuItem>
                                    <MenuItem value="AdHocAccess">AdHocAccess</MenuItem>
                                    <MenuItem value="DataProductSpecific">DataProductSpecific</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    label="Access Group Owner"
                                    fullWidth
                                    value={selectedGroup.owner || ''}
                                    onChange={(event) => handleInputChange('owner', event.target.value)}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    label="Associated ARC Role"
                                    fullWidth
                                    value={selectedGroup.associatedARCRole || ''}
                                    onChange={(event) => handleInputChange('associatedARCRole', event.target.value)}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    label="Associated AD / AAD Group"
                                    fullWidth
                                    value={selectedGroup.associatedADGroup || ''}
                                    onChange={(event) => handleInputChange('associatedADGroup', event.target.value)}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Autocomplete
                                    multiple
                                    options={aladdinGroups}
                                    value={selectedGroup.associatedAladdinGroups || []}
                                    onChange={(event, selectedValue) => handleInputChange('associatedAladdinGroups', selectedValue)}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Mapped Aladdin Groups" />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="subtitle1">Co-Owners</Typography>
                            </Grid>
                            {(selectedGroup.coOwners || []).map((coOwner, index) => (
                                <React.Fragment key={`co-owner-${index}`}>
                                    <Grid item xs={4}>
                                        <TextField
                                            label="Email"
                                            fullWidth
                                            required
                                            value={coOwner.email || ''}
                                            onChange={(event) => updateContact('coOwners', index, 'email', event.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={3}>
                                        <TextField
                                            label="First Name"
                                            fullWidth
                                            value={coOwner.firstName || ''}
                                            onChange={(event) => updateContact('coOwners', index, 'firstName', event.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={4}>
                                        <TextField
                                            label="Name"
                                            fullWidth
                                            value={coOwner.name || ''}
                                            onChange={(event) => updateContact('coOwners', index, 'name', event.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={1} sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Button color="error" onClick={() => removeContact('coOwners', index)}>Remove</Button>
                                    </Grid>
                                </React.Fragment>
                            ))}
                            <Grid item xs={12}>
                                <Button variant="outlined" onClick={() => addContact('coOwners')}>Add Co-Owner</Button>
                            </Grid>

                            <Grid item xs={12} sx={{ marginTop: 1 }}>
                                <Typography variant="subtitle1">Approvers</Typography>
                            </Grid>
                            {(selectedGroup.accessGroupApprovers || []).map((approver, index) => (
                                <React.Fragment key={`approver-${index}`}>
                                    <Grid item xs={4}>
                                        <TextField
                                            label="Email"
                                            fullWidth
                                            required
                                            value={approver.email || ''}
                                            onChange={(event) => updateContact('accessGroupApprovers', index, 'email', event.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={3}>
                                        <TextField
                                            label="First Name"
                                            fullWidth
                                            value={approver.firstName || ''}
                                            onChange={(event) => updateContact('accessGroupApprovers', index, 'firstName', event.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={4}>
                                        <TextField
                                            label="Name"
                                            fullWidth
                                            value={approver.name || ''}
                                            onChange={(event) => updateContact('accessGroupApprovers', index, 'name', event.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={1} sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Button color="error" onClick={() => removeContact('accessGroupApprovers', index)}>Remove</Button>
                                    </Grid>
                                </React.Fragment>
                            ))}
                            <Grid item xs={12}>
                                <Button variant="outlined" onClick={() => addContact('accessGroupApprovers')}>Add Approver</Button>
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    label="Associated Snowflake Role"
                                    fullWidth
                                    value={selectedGroup.associatedSnowflakeRole || ''}
                                    onChange={(event) => handleInputChange('associatedSnowflakeRole', event.target.value)}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    label="Associated Snowflake ARC Role Name"
                                    fullWidth
                                    value={selectedGroup.associatedSnowflakeArcRoleName || ''}
                                    onChange={(event) => handleInputChange('associatedSnowflakeArcRoleName', event.target.value)}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    label="Description"
                                    fullWidth
                                    multiline
                                    rows={4}
                                    value={selectedGroup.description || ''}
                                    onChange={(event) => handleInputChange('description', event.target.value)}
                                />
                            </Grid>

                            <Grid item xs={12} sx={{ marginTop: 2 }}>
                                <Button variant="contained" onClick={handleSave} disabled={loading} sx={{ marginRight: 2 }}>
                                    Save
                                </Button>
                                {!isNew && (
                                    <Button variant="contained" color="error" onClick={handleDelete} disabled={loading}>
                                        Delete
                                    </Button>
                                )}
                            </Grid>

                            {!isNew && (
                                <>
                                    <Grid item xs={12} sx={{ marginTop: 3 }}>
                                        <Typography variant="h6">Access Grants</Typography>
                                        <Typography variant="body2" sx={{ mt: 0.5 }}>Grant this group access to specific Data Products or Data Domains (CapabilityCategory). Enforcement will be applied once configured.</Typography>
                                    </Grid>

                                    {grantStatusMessage && (
                                        <Grid item xs={12}>
                                            <RequestStatusAlert message={grantStatusMessage} severity={grantSeverity} />
                                        </Grid>
                                    )}

                                    <Grid item xs={12} sx={{ marginTop: 1 }}>
                                        <Typography variant="subtitle1">Data Products</Typography>
                                    </Grid>
                                    {(accessGrants.productGrants || []).map((grant) => (
                                        <React.Fragment key={`product-grant-${grant.dataProductId}`}>
                                            <Grid item xs={10} sx={{ display: 'flex', alignItems: 'center' }}>
                                                <Typography variant="body2">{grant.dataProductName || grant.dataProductId}</Typography>
                                            </Grid>
                                            <Grid item xs={2} sx={{ display: 'flex', alignItems: 'center' }}>
                                                <Button color="error" size="small" onClick={() => handleRevokeProduct(grant.dataProductId)} disabled={loading}>
                                                    Remove
                                                </Button>
                                            </Grid>
                                        </React.Fragment>
                                    ))}
                                    <Grid item xs={10}>
                                        <Autocomplete
                                            options={availableProducts.filter(p => !(accessGrants.productGrants || []).some(g => g.dataProductId === p.id))}
                                            getOptionLabel={(option) => option.productFriendlyName || option.dataProductName || option.id}
                                            value={selectedProductToGrant}
                                            onChange={(_, value) => setSelectedProductToGrant(value)}
                                            renderInput={(params) => <TextField {...params} label="Add Data Product" size="small" />}
                                            isOptionEqualToValue={(option, value) => option.id === value.id}
                                        />
                                    </Grid>
                                    <Grid item xs={2} sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Button variant="outlined" onClick={handleGrantProduct} disabled={loading || !selectedProductToGrant} fullWidth>
                                            Grant
                                        </Button>
                                    </Grid>

                                    <Grid item xs={12} sx={{ marginTop: 2 }}>
                                        <Typography variant="subtitle1">Data Domains</Typography>
                                    </Grid>
                                    {(accessGrants.domainGrants || []).map((grant) => (
                                        <React.Fragment key={`domain-grant-${grant.domainName}`}>
                                            <Grid item xs={10} sx={{ display: 'flex', alignItems: 'center' }}>
                                                <Typography variant="body2">{grant.domainName}</Typography>
                                            </Grid>
                                            <Grid item xs={2} sx={{ display: 'flex', alignItems: 'center' }}>
                                                <Button color="error" size="small" onClick={() => handleRevokeDomain(grant.domainName)} disabled={loading}>
                                                    Remove
                                                </Button>
                                            </Grid>
                                        </React.Fragment>
                                    ))}
                                    <Grid item xs={10}>
                                        <Autocomplete
                                            options={availableDomains.filter(d => !(accessGrants.domainGrants || []).some(g => g.domainName === d))}
                                            value={selectedDomainToGrant}
                                            onChange={(_, value) => setSelectedDomainToGrant(value)}
                                            renderInput={(params) => <TextField {...params} label="Add Data Domain" size="small" />}
                                        />
                                    </Grid>
                                    <Grid item xs={2} sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Button variant="outlined" onClick={handleGrantDomain} disabled={loading || !selectedDomainToGrant} fullWidth>
                                            Grant
                                        </Button>
                                    </Grid>
                                </>
                            )}
                        </>
                    )}

                    <Backdrop sx={{ color: '#055A60', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
                        <CircularProgress color="inherit" />
                    </Backdrop>
                </Grid>
            )}
        </Grid>
    );
}

function AccessControlPanel({ value, index }) {
    const [file, setFile] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null);
    const [resultMessage, setResultMessage] = useState(null);
    const [optionValue, setOptionValue] = useState(null);

    const setFileToUpload = (event) => {
        setFile(event.target.files[0]);
    };

    const validate = () => {
        if (optionValue === null) {
            setErrorMessage('Please select a rules file type to upload.');
            return false;
        }

        if (file === null) {
            setErrorMessage('Please select a file to upload.');
            return false;
        }

        if (optionValue === 'data-products-access-rules' && file.name !== dataProductsAccessRulesFileName) {
            setErrorMessage(`Please select a valid file with name: ${dataProductsAccessRulesFileName} to upload.`);
            return false;
        }

        if (optionValue === 'custom-data-products-access-rules' && file.name !== customDataProductsAccessRulesFileName) {
            setErrorMessage(`Please select a valid file with name: ${customDataProductsAccessRulesFileName} to upload.`);
            return false;
        }

        return true;
    };

    const validateDownload = () => {
        if (optionValue === null) {
            setErrorMessage('Please select a rules file type to download.');
            return false;
        }
        return true;
    };

    const uploadFile = () => {
        setErrorMessage(null);
        setResultMessage(null);

        if (!validate()) return;

        doRequest(`/api/DataProductsPermissionRules?fileName=${file.name}`, null, {
            method: 'POST',
            body: file,
            headers: {
                'content-type': file.type,
                'content-length': `${file.size}`
            }
        })
            .then((response) => {
                if (!response.isError) {
                    setResultMessage('File uploaded successfully.');
                } else {
                    setErrorMessage('File upload failed. ' + response.data);
                }
            })
            .catch((error) => {
                setErrorMessage(error.message);
            });
    };

    const downloadPermissionRuleFile = () => {
        setErrorMessage(null);
        setResultMessage(null);

        if (!validateDownload()) return;

        const filename = `${optionValue}.json`;
        doRequest(`/api/DownloadPermissionRuleFile?fileName=${filename}`, null, {
            method: 'GET'
        })
            .then((response) => {
                if (!response.isError) {
                    downloadFile({ content: response.data, fileName: filename });
                } else {
                    setErrorMessage('Failed to download file.');
                }
            })
            .catch((error) => {
                setErrorMessage(error.message);
            });
    };

    return (
        <Grid item xs={12} hidden={value !== index}>
            {value === index && (
                <Grid item container xs={12} spacing={2}>
                    <Grid item xs={6}>
                        <span>Rules file type</span>
                        <br />
                        <RadioGroup aria-label="options" name="options" value={optionValue} onChange={(event) => setOptionValue(event.target.value)}>
                            <FormControlLabel
                                value="data-products-access-rules"
                                control={<Radio sx={{ color: 'primary.main', '&.Mui-checked': { color: 'secondary.main' } }} />}
                                label="Data products access rules"
                            />
                            <FormControlLabel
                                value="custom-data-products-access-rules"
                                control={<Radio sx={{ color: 'primary.main', '&.Mui-checked': { color: 'secondary.main' } }} />}
                                label="Custom data products access rules"
                            />
                        </RadioGroup>
                        <br />
                        <span>Select rules file to upload</span>
                        <br />
                        <br />
                        <input type="file" single="single" id="file" accept=".json" onChange={setFileToUpload} />
                        <br />
                        <br />
                        <Box display="flex" gap={2}>
                            <Button variant="contained" onClick={uploadFile}>Upload File</Button>
                            <Button variant="contained" onClick={downloadPermissionRuleFile}>Download File</Button>
                        </Box>
                        {errorMessage != null ? (
                            <Alert severity="error">
                                <AlertTitle>Error</AlertTitle>
                                {errorMessage}
                            </Alert>
                        ) : resultMessage != null ? (
                            <Alert severity="info">
                                <AlertTitle>Info</AlertTitle>
                                {resultMessage}
                            </Alert>
                        ) : null}
                    </Grid>
                </Grid>
            )}
        </Grid>
    );
}

function GrantsByProductDomainPanel({ value, index, idToken }) {
    const [subTab, setSubTab] = useState(0);
    const [availableProducts, setAvailableProducts] = useState([]);
    const [availableDomains, setAvailableDomains] = useState([]);
    const [allGroups, setAllGroups] = useState([]);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [selectedDomain, setSelectedDomain] = useState(null);
    const [groupsWithAccess, setGroupsWithAccess] = useState([]);
    const [selectedGroupToGrant, setSelectedGroupToGrant] = useState(null);
    const [loading, setLoading] = useState(false);
    const [statusMessage, setStatusMessage] = useState(null);
    const [statusSeverity, setStatusSeverity] = useState(null);

    useEffect(() => {
        if (value === index && idToken) {
            doRequest('/api/DataProducts', idToken).then(r => { if (!r.isError) setAvailableProducts(r.data || []); });
            doRequest('/api/dataaccessgroups/domains', idToken).then(r => { if (!r.isError) setAvailableDomains(r.data || []); });
            doRequest('/api/DataAccessGroups', idToken).then(r => { if (!r.isError) setAllGroups(r.data || []); });
        }
    }, [value, index, idToken]);

    useEffect(() => {
        setGroupsWithAccess([]);
        setSelectedGroupToGrant(null);
        setStatusMessage(null);
        if (!selectedProduct) return;
        setLoading(true);
        doRequest(`/api/dataaccessgroups/products/${selectedProduct.id}/grants`, idToken)
            .then(r => { if (!r.isError) setGroupsWithAccess(r.data || []); })
            .finally(() => setLoading(false));
    }, [selectedProduct]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        setGroupsWithAccess([]);
        setSelectedGroupToGrant(null);
        setStatusMessage(null);
        if (!selectedDomain) return;
        setLoading(true);
        doRequest(`/api/dataaccessgroups/domains/${encodeURIComponent(selectedDomain)}/grants`, idToken)
            .then(r => { if (!r.isError) setGroupsWithAccess(r.data || []); })
            .finally(() => setLoading(false));
    }, [selectedDomain]); // eslint-disable-line react-hooks/exhaustive-deps

    const refreshAccess = () => {
        if (subTab === 0 && selectedProduct) {
            setLoading(true);
            doRequest(`/api/dataaccessgroups/products/${selectedProduct.id}/grants`, idToken)
                .then(r => { if (!r.isError) setGroupsWithAccess(r.data || []); })
                .finally(() => setLoading(false));
        } else if (subTab === 1 && selectedDomain) {
            setLoading(true);
            doRequest(`/api/dataaccessgroups/domains/${encodeURIComponent(selectedDomain)}/grants`, idToken)
                .then(r => { if (!r.isError) setGroupsWithAccess(r.data || []); })
                .finally(() => setLoading(false));
        }
    };

    const handleGrant = async () => {
        if (!selectedGroupToGrant) return;
        setLoading(true);
        setStatusMessage(null);
        const isProduct = subTab === 0;
        const url = isProduct
            ? `/api/dataaccessgroups/${selectedGroupToGrant.id}/products`
            : `/api/dataaccessgroups/${selectedGroupToGrant.id}/domains`;
        const body = isProduct
            ? JSON.stringify({ dataProductId: selectedProduct.id })
            : JSON.stringify({ domainName: selectedDomain });
        const response = await doRequest(url, idToken, { method: 'POST', body });
        if (!response.isError) {
            setStatusMessage(isProduct ? 'Product access granted.' : 'Domain access granted.');
            setStatusSeverity('success');
            setSelectedGroupToGrant(null);
            refreshAccess();
        } else {
            setStatusMessage(isProduct ? 'Failed to grant product access.' : 'Failed to grant domain access.');
            setStatusSeverity('error');
            setLoading(false);
        }
    };

    const handleRevoke = async (group) => {
        if (!window.confirm(`Remove access for group "${group.groupName}"?`)) return;
        setLoading(true);
        setStatusMessage(null);
        const isProduct = subTab === 0;
        const url = isProduct
            ? `/api/dataaccessgroups/${group.groupId}/products/${selectedProduct.id}`
            : `/api/dataaccessgroups/${group.groupId}/domains/${encodeURIComponent(selectedDomain)}`;
        const response = await doRequest(url, idToken, { method: 'DELETE' });
        if (!response.isError) {
            setStatusMessage(isProduct ? 'Product access revoked.' : 'Domain access revoked.');
            setStatusSeverity('success');
            refreshAccess();
        } else {
            setStatusMessage(isProduct ? 'Failed to revoke product access.' : 'Failed to revoke domain access.');
            setStatusSeverity('error');
            setLoading(false);
        }
    };

    const grantedGroupIds = new Set(groupsWithAccess.map(g => g.groupId));
    const grantableGroups = allGroups.filter(g => !grantedGroupIds.has(g.id));
    const hasSelection = subTab === 0 ? !!selectedProduct : !!selectedDomain;

    return (
        <Grid item xs={12} hidden={value !== index}>
            {value === index && (
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Tabs value={subTab} onChange={(_, v) => { setSubTab(v); setSelectedProduct(null); setSelectedDomain(null); setGroupsWithAccess([]); setStatusMessage(null); }} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                            <Tab label="By Product" />
                            <Tab label="By Domain" />
                        </Tabs>
                    </Grid>

                    {subTab === 0 && (
                        <>
                            <Grid item xs={12}>
                                <Typography variant="body2">Select a Data Product to see which access groups have access, then add or remove groups.</Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Autocomplete
                                    options={availableProducts}
                                    getOptionLabel={(o) => o.productFriendlyName || o.dataProductName || o.id}
                                    value={selectedProduct}
                                    onChange={(_, v) => setSelectedProduct(v)}
                                    renderInput={(params) => <TextField {...params} label="Select Data Product" />}
                                    isOptionEqualToValue={(o, v) => o.id === v.id}
                                />
                            </Grid>
                        </>
                    )}

                    {subTab === 1 && (
                        <>
                            <Grid item xs={12}>
                                <Typography variant="body2">Select a Data Domain to see which access groups have access, then add or remove groups.</Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Autocomplete
                                    options={availableDomains}
                                    value={selectedDomain}
                                    onChange={(_, v) => setSelectedDomain(v)}
                                    renderInput={(params) => <TextField {...params} label="Select Data Domain" />}
                                />
                            </Grid>
                        </>
                    )}

                    {statusMessage && (
                        <Grid item xs={12}>
                            <RequestStatusAlert message={statusMessage} severity={statusSeverity} />
                        </Grid>
                    )}

                    {hasSelection && (
                        <>
                            <Grid item xs={12} sx={{ mt: 1 }}>
                                <Typography variant="subtitle1">Groups with Access</Typography>
                            </Grid>
                            {groupsWithAccess.length === 0 && !loading && (
                                <Grid item xs={12}>
                                    <Typography variant="body2" color="text.secondary">No groups have been granted access.</Typography>
                                </Grid>
                            )}
                            {groupsWithAccess.map((group) => (
                                <React.Fragment key={group.groupId}>
                                    <Grid item xs={10} sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Typography variant="body2">{group.groupName}</Typography>
                                    </Grid>
                                    <Grid item xs={2} sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Button color="error" size="small" onClick={() => handleRevoke(group)} disabled={loading}>
                                            Remove
                                        </Button>
                                    </Grid>
                                </React.Fragment>
                            ))}

                            <Grid item xs={10}>
                                <Autocomplete
                                    options={grantableGroups}
                                    getOptionLabel={(o) => o.name || o.id}
                                    value={selectedGroupToGrant}
                                    onChange={(_, v) => setSelectedGroupToGrant(v)}
                                    renderInput={(params) => <TextField {...params} label="Add Access Group" size="small" />}
                                    isOptionEqualToValue={(o, v) => o.id === v.id}
                                />
                            </Grid>
                            <Grid item xs={2} sx={{ display: 'flex', alignItems: 'center' }}>
                                <Button variant="outlined" onClick={handleGrant} disabled={loading || !selectedGroupToGrant} fullWidth>
                                    Grant
                                </Button>
                            </Grid>
                        </>
                    )}

                    <Backdrop sx={{ color: '#055A60', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
                        <CircularProgress color="inherit" />
                    </Backdrop>
                </Grid>
            )}
        </Grid>
    );
}

const DataAccessAdminPage = ({ idToken, drawerWidth }) => {
    const [tab, setTab] = useState(0);

    return (
        <Grid container spacing={2} style={{ width: 'auto', marginTop: '2px', marginLeft: drawerWidth - 15 }}>
            <Grid item xs={12}>
                <Typography variant="h5">Data Access Control Admin</Typography>
            </Grid>
            <Grid item xs={12} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={tab} onChange={(event, newValue) => setTab(newValue)}>
                    <Tab label="Data Access Groups" />
                    <Tab label="Access Grants by Product/Domain" />
                    <Tab label="Data Access Control" />
                </Tabs>
            </Grid>
            <Grid container item xs={12}>
                <DataAccessGroupPanel value={tab} index={0} idToken={idToken} />
                <GrantsByProductDomainPanel value={tab} index={1} idToken={idToken} />
                <AccessControlPanel value={tab} index={2} />
            </Grid>
        </Grid>
    );
};

export default DataAccessAdminPage;
